import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { NotificationItem } from './NotificationItem';
import { Badge } from '@/components/ui/badge';

// Debounce delay for realtime events (ms)
const DEBOUNCE_DELAY = 500;

export const NotificationBell = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [open, setOpen] = useState(false);

  // Refs for debouncing and cleanup
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Fetch only unread count using optimized RPC
   * Used on mount - lazy load full notifications when dropdown opens
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!userProfile?.id) return;

    try {
      const { data, error } = await supabase.rpc(
        'get_unread_notification_count',
        { p_user_id: userProfile.id }
      );

      if (error) throw error;

      if (mountedRef.current) {
        setUnreadCount(data || 0);
      }
    } catch (error) {
      console.error('[NotificationBell] Error fetching unread count:', error);
    }
  }, [userProfile?.id]);

  /**
   * Fetch full notifications - only called when dropdown opens
   */
  const fetchNotifications = useCallback(async () => {
    if (!userProfile?.id) return;

    setLoading(true);

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          actor:actor_id (
            id,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('user_id', userProfile.id)
        .order('created_date', { ascending: false })
        .limit(20);

      if (error) throw error;

      if (mountedRef.current) {
        setNotifications(data || []);
        setUnreadCount(data?.filter(n => !n.is_read).length || 0);
        setHasFetched(true);
      }
    } catch (error) {
      console.error('[NotificationBell] Error fetching notifications:', error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [userProfile?.id]);

  /**
   * Debounced fetch for realtime events
   */
  const debouncedFetchUnreadCount = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchUnreadCount();
    }, DEBOUNCE_DELAY);
  }, [fetchUnreadCount]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_date: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state using functional updates to avoid stale closures
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, is_read: true, read_date: new Date().toISOString() }
            : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('[NotificationBell] Error marking notification as read:', error);
    }
  }, []);

  /**
   * Mark all as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!userProfile?.id) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_read: true,
          read_date: new Date().toISOString()
        })
        .eq('user_id', userProfile.id)
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({
          ...n,
          is_read: true,
          read_date: new Date().toISOString()
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('[NotificationBell] Error marking all as read:', error);
    }
  }, [userProfile?.id]);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      // Update local state using functional updates
      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId);
        const wasUnread = notification && !notification.is_read;

        if (wasUnread) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }

        return prev.filter(n => n.id !== notificationId);
      });
    } catch (error) {
      console.error('[NotificationBell] Error deleting notification:', error);
    }
  }, []);

  /**
   * Handle dropdown open - lazy load notifications
   */
  const handleOpenChange = useCallback((isOpen) => {
    setOpen(isOpen);

    // Lazy load notifications when opening for the first time
    if (isOpen && !hasFetched) {
      fetchNotifications();
    }
  }, [hasFetched, fetchNotifications]);

  /**
   * Navigate to settings
   */
  const handleSettingsClick = useCallback(() => {
    setOpen(false);
    navigate('/settings');
  }, [navigate]);

  /**
   * Close dropdown handler
   */
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // Initial unread count fetch (lightweight)
  useEffect(() => {
    mountedRef.current = true;
    fetchUnreadCount();

    return () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fetchUnreadCount]);

  // Realtime subscription with auth token setup (fixes race condition)
  useEffect(() => {
    if (!userProfile?.id) return;

    let channel = null;

    const setupSubscription = async () => {
      // Set auth token for private channel first
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await supabase.realtime.setAuth(session.access_token);
      }

      // Now set up subscription
      channel = supabase
        .channel(`notifications:${userProfile.id}`, {
          config: { private: true }
        })
        .on('broadcast', { event: 'INSERT' }, (payload) => {
          const newNotification = payload.payload.record;

          // If dropdown has been opened, add to list
          if (hasFetched) {
            setNotifications(prev => [newNotification, ...prev].slice(0, 20));
          }
          setUnreadCount(prev => prev + 1);
        })
        .on('broadcast', { event: 'UPDATE' }, (payload) => {
          const updatedNotification = payload.payload.record;

          // Update in list if we have loaded notifications
          if (hasFetched) {
            setNotifications(prev =>
              prev.map(n => n.id === updatedNotification.id ? updatedNotification : n)
            );
          }

          // Debounced refresh of count
          debouncedFetchUnreadCount();
        })
        .on('broadcast', { event: 'DELETE' }, (payload) => {
          const deletedId = payload.payload.old_record?.id;

          if (hasFetched && deletedId) {
            // Use functional update to avoid stale closure
            setNotifications(prev => {
              const notification = prev.find(n => n.id === deletedId);
              if (notification && !notification.is_read) {
                setUnreadCount(prevCount => Math.max(0, prevCount - 1));
              }
              return prev.filter(n => n.id !== deletedId);
            });
          } else {
            // Just refresh count
            debouncedFetchUnreadCount();
          }
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('[NotificationBell] Subscribed to notifications');
          }
        });
    };

    setupSubscription();

    return () => {
      if (channel) {
        channel.unsubscribe();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [userProfile?.id, hasFetched, debouncedFetchUnreadCount]);

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-9 w-9 p-0"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-96"
        onInteractOutside={handleClose}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              Mark all as read
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleSettingsClick}
              aria-label="Notification settings"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">Loading...</div>
            </div>
          ) : !hasFetched ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-500">Loading notifications...</div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <Bell className="h-12 w-12 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500 text-center">No notifications yet</p>
              <p className="text-xs text-gray-400 text-center mt-1">
                You'll see notifications here when someone mentions you or interacts with your content
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                  onClose={handleClose}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
